import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  useTheme,
  TextField,
  Chip,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
} from "@mui/material";
import { Search, Clear } from "@mui/icons-material";
import Header from "components/Header";
import FlexBetween from "components/FlexBetween";
import { DataGrid } from "@mui/x-data-grid";
import DataGridCustomToolbar from "components/DataGridCustomToolbar";
import {
  getReportedIssues,
  updateIssueStatus,
} from "service/admin/contentAdminService";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import moment from "moment";

const STATUS_OPTIONS = [
  { value: "open", label: "Open", color: "warning" },
  { value: "in_progress", label: "In Progress", color: "info" },
  { value: "resolved", label: "Resolved", color: "success" },
  { value: "closed", label: "Closed", color: "default" },
];

const statusChip = (status) => {
  const cfg = STATUS_OPTIONS.find((s) => s.value === status) || {
    label: status || "-",
    color: "default",
  };
  return <Chip label={cfg.label} size="small" color={cfg.color} />;
};

const IssueDetailDialog = ({ issue, open, onClose, onStatusUpdated }) => {
  const theme = useTheme();
  const [selectedStatus, setSelectedStatus] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && issue) {
      setSelectedStatus(issue.status || "open");
    }
  }, [open, issue]);

  const handleSave = async () => {
    try {
      setSaving(true);
      await updateIssueStatus(issue.id, selectedStatus);
      toast.success("Issue status updated.");
      onStatusUpdated(issue.id, selectedStatus);
      onClose();
    } catch (error) {
      toast.error("Failed to update issue status.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Issue Detail</DialogTitle>
      <DialogContent dividers>
        {issue ? (
          <Grid container spacing={1}>
            {[
              [
                "Reporter",
                issue.athlete?.full_name ||
                  issue.reporter?.full_name ||
                  issue.user_id ||
                  "-",
              ],
              ["Issue Type", issue.issue_type || issue.category || "-"],
              ["Created At", issue.created_at ? moment(issue.created_at).format("DD MMM YYYY HH:mm") : "-"],
            ].map(([label, value]) => (
              <React.Fragment key={label}>
                <Grid item xs={4}>
                  <Typography
                    variant="body2"
                    color={theme.palette.secondary[300]}
                  >
                    {label}
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  <Typography
                    variant="body2"
                    color={theme.palette.secondary[100]}
                  >
                    {value}
                  </Typography>
                </Grid>
              </React.Fragment>
            ))}
            <Grid item xs={12} sx={{ mt: 1 }}>
              <Typography
                variant="body2"
                color={theme.palette.secondary[300]}
                gutterBottom
              >
                Description
              </Typography>
              <Typography
                variant="body2"
                color={theme.palette.secondary[100]}
                sx={{
                  whiteSpace: "pre-wrap",
                  background: theme.palette.primary.light,
                  p: 1.5,
                  borderRadius: 1,
                }}
              >
                {issue.description || "-"}
              </Typography>
            </Grid>
            <Grid item xs={12} sx={{ mt: 2 }}>
              <FormControl size="small" color="secondary" fullWidth>
                <InputLabel color="secondary">Update Status</InputLabel>
                <Select
                  label="Update Status"
                  color="secondary"
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                >
                  {STATUS_OPTIONS.map((s) => (
                    <MenuItem key={s.value} value={s.value}>
                      {s.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Close
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleSave}
          disabled={saving || !issue}
        >
          {saving ? "Saving..." : "Save Status"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const Support = () => {
  const theme = useTheme();
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [rowCount, setRowCount] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = {
        page: page + 1,
        limit: pageSize,
        search: search || undefined,
        status: statusFilter !== "all" ? statusFilter : undefined,
      };
      const response = await getReportedIssues(params);
      const data = response.data.data;
      setRows(Array.isArray(data) ? data : data.data || []);
      if (data.pagination?.total !== undefined) setRowCount(data.pagination.total);
    } catch (error) {
      toast.error("Failed to load support issues.");
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize, search, statusFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearch = () => {
    setPage(0);
    setSearch(searchInput);
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setSearch("");
    setPage(0);
  };

  const handleStatusUpdated = (id, newStatus) => {
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, status: newStatus } : r))
    );
  };

  const columns = [
    {
      field: "sno",
      headerName: "S.NO",
      flex: 0.4,
      sortable: false,
      renderCell: (params) =>
        page * pageSize + params.api.getRowIndex(params.id) + 1,
    },
    {
      field: "reporter",
      headerName: "REPORTER",
      flex: 1.2,
      renderCell: (params) =>
        params.row.athlete?.full_name ||
        params.row.reporter?.full_name ||
        params.row.user_id ||
        "-",
    },
    {
      field: "issue_type",
      headerName: "ISSUE TYPE",
      flex: 1,
      renderCell: (params) =>
        params.value || params.row.category || "-",
    },
    {
      field: "description",
      headerName: "DESCRIPTION",
      flex: 1.8,
      renderCell: (params) => {
        const desc = params.value || "";
        return desc.length > 60 ? desc.slice(0, 60) + "..." : desc || "-";
      },
    },
    {
      field: "status",
      headerName: "STATUS",
      flex: 0.9,
      renderCell: (params) => statusChip(params.value),
    },
    {
      field: "created_at",
      headerName: "CREATED AT",
      flex: 1,
      renderCell: (params) =>
        params.value ? moment(params.value).format("DD MMM YYYY HH:mm") : "-",
    },
  ];

  return (
    <>
      <ToastContainer />
      <Box m="1.5rem 2.5rem">
        <FlexBetween mb={1}>
          <Header
            title="SUPPORT ISSUES"
            subTitle="Manage reported issues and support tickets"
          />
        </FlexBetween>

        <Box mt="1rem" mb="0.5rem" display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <TextField
            size="small"
            placeholder="Search issues..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            color="secondary"
            sx={{ width: 260 }}
            InputProps={{
              endAdornment: searchInput ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleClearSearch}>
                    <Clear fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <IconButton onClick={handleSearch} color="secondary">
            <Search />
          </IconButton>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel color="secondary">Status</InputLabel>
            <Select
              color="secondary"
              label="Status"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="all">All</MenuItem>
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s.value} value={s.value}>
                  {s.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box
          mt="10px"
          height="65vh"
          sx={{
            "& .MuiDataGrid-root": { border: "none" },
            "& .MuiDataGrid-cell": { borderBottom: "none" },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: theme.palette.background.alt,
              color: theme.palette.secondary[100],
              borderBottom: "none",
            },
            "& .MuiDataGrid-virtualScroller": {
              backgroundColor: theme.palette.primary.light,
            },
            "& .MuiDataGrid-footerContainer": {
              backgroundColor: theme.palette.background.alt,
              color: theme.palette.secondary[100],
              borderTop: "none",
            },
            "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
              color: `${theme.palette.secondary[200]} !important`,
            },
          }}
        >
          <DataGrid
            loading={isLoading}
            getRowId={(row) => row.id}
            rows={rows}
            columns={columns}
            rowCount={rowCount || rows.length}
            rowsPerPageOptions={[10, 25, 50]}
            pagination
            page={page}
            pageSize={pageSize}
            paginationMode={rowCount > 0 ? "server" : "client"}
            onPageChange={(newPage) => setPage(newPage)}
            onPageSizeChange={(newSize) => {
              setPageSize(newSize);
              setPage(0);
            }}
            onRowClick={(params) => {
              setSelectedIssue(params.row);
              setDetailOpen(true);
            }}
            components={{ Toolbar: DataGridCustomToolbar }}
            sx={{
              cursor: "pointer",
              "& .MuiCircularProgress-circle": {
                color: theme.palette.secondary[300],
              },
            }}
          />
        </Box>
      </Box>

      <IssueDetailDialog
        open={detailOpen}
        issue={selectedIssue}
        onClose={() => setDetailOpen(false)}
        onStatusUpdated={handleStatusUpdated}
      />
    </>
  );
};

export default Support;
